import type { Amenity } from "@/types/Amenity";

export interface BookedAmenityRef {
  amenityId: string;
}

/**
 * Resolves a booking's `bookedAmenities` refs against the live list of amenities at the
 * location. Some refs may be stale — e.g. an amenity that was on a booking when it was
 * created has since been removed at the location level. Stale refs are reported separately
 * so the caller can warn / explain in the UI rather than silently filtering them out (which
 * is what caused the "stuck on Loading amenities…" bug — the caller couldn't tell whether
 * empty meant "still loading" or "all refs unresolvable").
 */
export function resolveBookedAmenities(
  bookedAmenities: ReadonlyArray<BookedAmenityRef> | null | undefined,
  locationAmenities: ReadonlyArray<Amenity>,
): { resolved: Amenity[]; stale: string[] } {
  if (!bookedAmenities || bookedAmenities.length === 0) {
    return { resolved: [], stale: [] };
  }
  const byId = new Map(locationAmenities.map((a) => [a.id, a]));
  const resolved: Amenity[] = [];
  const stale: string[] = [];
  for (const ref of bookedAmenities) {
    const amenity = byId.get(ref.amenityId);
    if (amenity) resolved.push(amenity);
    else stale.push(ref.amenityId);
  }
  return { resolved, stale };
}
